import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Surface } from "@/components/ds";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/config`)
      .then(({ data }) => setRegistrationOpen(!!data.registration_open))
      .catch(() => setRegistrationOpen(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-12">
      <div className="w-full max-w-sm" data-testid="login-page">
        <div className="mb-8 flex items-center gap-3">
          <img
            src="/brand/tr3nd-logo.jpg"
            alt="TR3ND Studio"
            data-testid="login-logo"
            className="h-14 w-14 shrink-0 rounded-lg object-contain"
          />
          <div>
            <h1 className="font-display text-2xl font-semibold leading-none">TR3ND OS</h1>
            <p className="mt-1 text-xs text-muted-foreground">Gestão para impressão 3D</p>
          </div>
        </div>

        <Surface className="p-6">
          <h2 className="font-display text-lg">
            {mode === "login" ? "Entrar na sua conta" : "Criar sua conta"}
          </h2>
          <form onSubmit={submit} className="mt-5 flex flex-col gap-4">
            {mode === "register" && (
              <Field label="Seu nome">
                <Input
                  value={form.name}
                  onChange={set("name")}
                  required
                  placeholder="Ex.: Maria"
                  data-testid="register-name-input"
                />
              </Field>
            )}
            <Field label="E-mail">
              <Input
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                placeholder="voce@email.com"
                data-testid="login-email-input"
              />
            </Field>
            <Field label="Senha">
              <Input
                type="password"
                value={form.password}
                onChange={set("password")}
                required
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </Field>

            {error && (
              <p
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-red-300"
                data-testid="login-error"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="mt-1 h-11 rounded-full text-sm font-semibold transition-transform active:scale-95"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "login" ? "Entrar" : "Criar conta"}
            </Button>
          </form>

          {registrationOpen ? (
            <button
              type="button"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              data-testid="toggle-auth-mode-btn"
              className="mt-5 w-full text-center text-xs text-muted-foreground transition-colors hover:text-brand"
            >
              {mode === "login" ? "Primeiro acesso — criar conta" : "Já tenho conta — entrar"}
            </button>
          ) : (
            <p className="mt-5 text-center text-xs text-muted-foreground" data-testid="login-help-text">
              Parceiro? Use o e-mail e a senha que a TR3ND enviou para você.
            </p>
          )}
        </Surface>
      </div>
    </div>
  );
}
